import * as XLSX from "xlsx-js-style";

import { Button, Col, Form, Row, Table } from "react-bootstrap";
import React, { useContext, useState, useCallback, useRef, useEffect } from "react";
import {
  faCircleCheck,
  faCircleXmark,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MainContext } from "../App";

// Format phone number as +90 (xxx) xxx xx xx
const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+90 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }
  return phone;
};

export const RoutesTab = ({
  projectName,
  vehicles,
  onVehicleClick
}) => {
  const { signedInUserEmail } = useContext(MainContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [fontSize, setFontSize] = useState(14);
  const [autoFontSize, setAutoFontSize] = useState(14);
  const [useAutoFontSize, setUseAutoFontSize] = useState(true);
  const tableRef = useRef(null);
  const [lastClickedIcon, setLastClickedIcon] = useState(null);

  const exportToExcel = () => {
    if (vehicles.length === 0) return;

    const newWs = XLSX.utils.aoa_to_sheet([]);
    const title = `${projectName} Güzergah Listesi`;
    XLSX.utils.sheet_add_aoa(newWs, [[title]], { origin: "A1" });
    XLSX.utils.sheet_add_aoa(newWs, [[""]], { origin: "A2" });
    newWs["A2"].s = {
      fill: {
        fgColor: { rgb: "ed545c" },
      },
    };

    const links1 = [];
    const links2 = [];
    const links3 = [];
    XLSX.utils.sheet_add_json(
      newWs,
      vehicles?.map((vehicle) => {
        links1.push(
          `https://${
            window.location.host
          }/driverDetail?driverId=${vehicle.driverId}&driverName=${encodeURIComponent(vehicle.driverName || '')}`
        );
        links2.push(
          `https://${
            window.location.host
          }/vehicleDetailPart?vehicleId=${vehicle.vehicleId}&plate=${encodeURIComponent(vehicle.plate || '')}`
        );
        links3.push(
          `https://${window.location.host}/vehicleDetail/${
            vehicle.vehicleId
          }?routeId=${vehicle._id}`
        );
        return {
          Güzergah: vehicle.name ?? '-',
          Sürücü: vehicle.driverName ?? '-',
          "Tel no.": formatPhoneNumber(vehicle.driverPhone) ?? '-',
          Plaka: vehicle.plate ?? '-',
          "GPS Link": `Tıkla ${vehicle?.gpsUrl ? "✔" : "✖"}`,
        };
      }),
      { origin: "A3" }
    );

    // Merge cells for the title
    const merge = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    if (!newWs["!merges"]) newWs["!merges"] = [];
    newWs["!merges"] = newWs["!merges"].concat(merge);

    const merge3 = [{ s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }];
    if (!newWs["!merges"]) newWs["!merges"] = [];
    newWs["!merges"] = newWs["!merges"].concat(merge3);

    // Apply styling to the title
    newWs["A1"].s = {
      font: {
        name: "Arial",
        sz: 20,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "14345c" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };

    const wStyle = {
      font: {
        name: "Arial",
        sz: 10,
        color: { rgb: "ffffff" },
      },
      fill: {
        fgColor: { rgb: "43648c" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: {
          style: "medium",
          color: { rgb: "000000" },
        },
        bottom: {
          style: "medium",
          color: { rgb: "000000" },
        },
        left: {
          style: "medium",
          color: { rgb: "000000" },
        },
        right: {
          style: "medium",
          color: { rgb: "000000" },
        },
      },
    };

    const wStyleH = {
      font: {
        name: "Arial",
        sz: 10,
        color: { rgb: "ffffff" },
      },
      fill: {
        fgColor: { rgb: "14345c" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: {
          style: "medium",
          color: { rgb: "000000" },
        },
        bottom: {
          style: "medium",
          color: { rgb: "000000" },
        },
        left: {
          style: "medium",
          color: { rgb: "000000" },
        },
        right: {
          style: "medium",
          color: { rgb: "000000" },
        },
      },
    };

    const paint = ["A", "B", "C", "D", "E"];
    paint.forEach((el) => {
      if (!newWs[`${el}3`]) newWs[`${el}3`] = {};
      newWs[`${el}3`].s = wStyleH;

      for (let i = 4; i < vehicles.length + 4; i++) {
        if (!newWs[`${el}${i}`]) newWs[`${el}${i}`] = {};
        newWs[`${el}${i}`].s = wStyle;

        if (el === "B") {
          if (!newWs[`${el}${i}`]) newWs[`${el}${i}`] = {};
          newWs[`${el}${i}`].l = {
            Target: links1[i - 4],
            Tooltip: "Sürücü evrakları",
          };
        }

        if (el === "D") {
          if (!newWs[`${el}${i}`]) newWs[`${el}${i}`] = {};
          newWs[`${el}${i}`].l = {
            Target: links2[i - 4],
            Tooltip: "Araç evrakları",
          };
        }

        if (el === "E") {
          if (!newWs[`${el}${i}`]) newWs[`${el}${i}`] = {};
          newWs[`${el}${i}`].l = { Target: links3[i - 4], Tooltip: `GPS` };
        }

        if (el === "A" || el === "B") {
          newWs[`${el}${i}`].s = {
            ...wStyle,
            alignment: { horizontal: "left", vertical: "center" },
          };
        }
      }
    });

    var wscols = [
      { wch: 50 },
      { wch: 35 },
      { wch: 22 },
      { wch: 15 },
      { wch: 20 },
    ];

    newWs["!cols"] = wscols;

    const createdByRow = vehicles.length + 4;
    const createdBy = `${
      signedInUserEmail ?? "-"
    } tarafından ${new Date().toLocaleDateString()} tarihinde oluşturuldu.`;

    XLSX.utils.sheet_add_aoa(newWs, [[createdBy]], {
      origin: `A${createdByRow}`,
    });

    newWs[`A${createdByRow}`].s = {
      font: {
        name: "Arial",
        sz: 11,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "000000" },
      },
      alignment: {
        horizontal: "right",
        vertical: "center",
      },
    };

    const merge2 = [
      { s: { r: createdByRow - 1, c: 0 }, e: { r: createdByRow - 1, c: 4 } },
    ];
    if (!newWs["!merges"]) newWs["!merges"] = [];
    newWs["!merges"] = newWs["!merges"].concat(merge2);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, newWs, "Ana sayfa");
    XLSX.writeFile(wb, `${projectName}-güzergah-listesi.xlsx`);
  };

  // Function to change font size
  const changeFontSize = useCallback((delta) => {
    setFontSize((prevSize) => {
      const newSize = prevSize + delta;
      return Math.max(10, Math.min(20, newSize));
    });
    setUseAutoFontSize(false);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current && useAutoFontSize) {
        const tableWidth = tableRef.current.offsetWidth;
        const newFontSize = Math.max(10, Math.min(14, tableWidth / 50));
        setAutoFontSize(newFontSize);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [useAutoFontSize]);

  const effectiveFontSize = useAutoFontSize ? autoFontSize : fontSize;

  return (
    <div style={{ padding: "20px 0" }}>
      <Row className="mb-3">
        <Col xs={12} sm={6} className="mb-2 mb-sm-0">
          <Form.Control
            type="text"
            placeholder="Güzergah ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(20, 52, 92, 0.5)",
              color: "white"
            }}
          />
        </Col>
        <Col xs={12} sm={6} className="d-flex justify-content-sm-end align-items-center">
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
            <Button
              size="sm"
              onClick={() => changeFontSize(-1)}
              style={{
                marginRight: '5px',
                background: "rgba(20, 52, 92, 0.2)",
                border: "1px solid rgba(20, 52, 92, 0.5)",
                color: "#5a9bd4"
              }}
            >
              A-
            </Button>
            <span style={{ margin: '0 5px', fontSize: '0.9rem', color: '#5a9bd4' }}>{effectiveFontSize.toFixed(1)}px</span>
            <Button
              size="sm"
              onClick={() => changeFontSize(1)}
              style={{
                background: "rgba(20, 52, 92, 0.2)",
                border: "1px solid rgba(20, 52, 92, 0.5)",
                color: "#5a9bd4"
              }}
            >
              A+
            </Button>
          </div>
          <Button
            onClick={exportToExcel}
            style={{
              background: "linear-gradient(135deg, #14345c 0%, #1e5a8a 100%)",
              border: "none",
              color: "#fff",
              fontWeight: "700",
              boxShadow: "0 4px 15px rgba(20, 52, 92, 0.5)"
            }}
          >
            Excel indir
          </Button>
        </Col>
      </Row>
      <div style={{ overflowX: "auto", height: "calc(100vh - 280px)" }}>
        <Table
          ref={tableRef}
          striped
          bordered
          hover
          variant="dark"
          style={{
            tableLayout: "auto",
            marginTop: 10,
            marginBottom: 10,
            fontSize: `${effectiveFontSize}px`,
            borderColor: "rgba(20, 52, 92, 0.4)"
          }}
        >
          <thead style={{
            background: "#000000",
            borderBottom: "2px solid rgba(20, 52, 92, 0.6)"
          }}>
            <tr>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>Güzergah</th>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>Sürücü</th>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>Tel no.</th>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>Plaka</th>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>Koltuk sayısı</th>
              <th style={{ color: "#5a9bd4", fontWeight: "700" }}>GPS</th>
            </tr>
          </thead>
          <tbody>
            {vehicles
              ?.filter((el) =>
                el?.name?.toLocaleLowerCase('tr').includes(searchTerm.toLocaleLowerCase('tr'))
              )
              ?.map((route, idx) => (
                <tr key={route.vehicleId + idx}>
                  <td style={{ fontWeight: '700', fontSize: '1.1em' }} title={route.name}>{route.name}</td>
                  <td>
                    {route.driverName ? (
                      <Button
                        variant="link"
                        style={{ padding: 0, fontSize: 'inherit' }}
                        onClick={() => {
                          window.open(
                            `http://${window.location.host}/driverDetail?driverId=${route.driverId}&driverName=${encodeURIComponent(route.driverName || '')}`,
                            `_blank`,
                            "width=900,height=900,popup=true"
                          );
                        }}
                      >
                        {route.driverName}
                      </Button>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {route.driverPhone ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ width: "165px", display: "inline-block" }}>
                          <a href={`tel:+90${route.driverPhone}`} style={{ whiteSpace: "nowrap" }}>
                            {formatPhoneNumber(route.driverPhone)}
                          </a>
                        </span>
                        <FontAwesomeIcon
                          className={`copy-icon ${lastClickedIcon === `phone-${route.vehicleId}` ? 'animate-copy' : ''}`}
                          icon={faCopy}
                          color="#5a9bd4"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigator.clipboard.writeText(`+90${route.driverPhone}`);
                            setLastClickedIcon(`phone-${route.vehicleId}`);
                            setTimeout(() => setLastClickedIcon(null), 500);
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-muted">
                        {"-"}
                      </span>
                    )}
                  </td>
                  <td>
                    {route.plate ? (
                      <Button
                        variant="link"
                        style={{ padding: 0, fontSize: 'inherit' }}
                        onClick={() => {
                          let doc = window.open(
                            `http://${
                              window.location.host
                            }/vehicleDetailPart?vehicleId=${route.vehicleId}&plate=${encodeURIComponent(route.plate || '')}`,
                            `${route.plate}`,
                            "width=900,height=750"
                          );
                          doc.title = route.plate;
                        }}
                      >
                        {route.plate}
                      </Button>
                    ) : (
                      <span className="text-muted">
                        {"-"}
                      </span>
                    )}
                  </td>
                  <td>
                    {route.capacity ? (
                      <span>{route.capacity}</span>
                    ) : (
                      <span className="text-muted">
                        {"-"}
                      </span>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="link"
                      onClick={() => onVehicleClick(route)}
                    >
                      <div
                        title="GPS"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span className="disappear-on-small">GPS</span>
                        {route?.gpsUrl ? (
                          <FontAwesomeIcon
                            icon={faCircleCheck}
                            color="green"
                            style={{ marginLeft: 5 }}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            color="red"
                            style={{ marginLeft: 5 }}
                          />
                        )}
                      </div>
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
